import { Link } from "@tanstack/react-router"
import { Compass, Home } from "lucide-react"
import Header from "@/components/Header"
import { PublicFooter } from "@/components/PublicFooter"
import { Button } from "@/components/ui/button"
import { useDocumentTitle, useHeadTags } from "@/hooks/use-head"
import { createTitle } from "@/lib/seo"

// The app-wide 404. Wired as the router's defaultNotFoundComponent, so it
// renders for any unmatched path inside the root Outlet — which means it carries
// no layout chrome of its own. We mount the plain Header + PublicFooter here to
// match the marketing/public shell (trip/guide chrome would be wrong for a dead
// link). The app is SPA (ssr:false), so the head tags are client-only; we still
// emit robots=noindex so a crawler that renders the JS won't index the 404.
export function NotFound() {
  useDocumentTitle(createTitle(`Page not found`))
  useHeadTags(
    [{ tag: `meta`, attrs: { name: `robots`, content: `noindex` } }],
    `notfound-noindex`
  )

  return (
    <div className="flex flex-col min-h-dvh">
      <Header />
      <main className="flex-1 pt-[3.25rem] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-6 py-16">
          <p className="font-display text-7xl font-extrabold tracking-tight text-primary">
            404
          </p>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight">
              We couldn't find that page
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              The link may be broken, or the trip or guide may have been moved or
              deleted. Let's get you back on the map.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Button asChild className="rounded-full px-6">
              <Link to="/">
                <Home className="size-4" />
                Back home
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-6">
              <Link to="/trips/explore">
                <Compass className="size-4" />
                Explore trips
              </Link>
            </Button>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}
